import { ActionIcon, Avatar, Box, Button, CopyButton, Group, Skeleton, Table, Text, Tooltip } from "@mantine/core"
import { ArrowLeftIcon, CheckIcon, CopyIcon, ShoppingCartSimpleIcon, TagIcon, TrendUpIcon, WarningCircleIcon } from "@phosphor-icons/react"
import React from "react"
import { useTranslation } from "react-i18next"
import { HelpIcon } from "@dehub/theme"
import { useDispatch, useSelector } from "react-redux"
import { useNavigate } from "react-router"
import * as PricingApi from "../../apis/pricing"
import type { PriceBreakdown } from "../../apis/pricing"
import { IAuth } from "../../store/Auth"
import {
  addToCart as addToCartAction,
  ICart,
  updatePrice as updatePriceAction,
  updateQuantity as updateQuantityAction,
} from "../../store/Cart"
import { currencyFormat } from "../../utils"
import { renderBusinessName } from "../../utils/render-business-name"
import { DotPill, type Tone } from "../delivery/atoms"
import { PageLayout } from "../layout"
import { ReasonTooltip } from "../ui"
import RenderText from "../ui/render-text"
import { ProductGallery } from "./product-gallery"
import classes from "./product-detail.module.css"
import { addBlockReason, describePrice, describePromotion, formatDate, promoIcon } from "./product-list/price-labels"
import { QtyStepper } from "./product-list/QtyStepper"
import { StockLabel } from "./product-list/StockLabel"
import { cleanName, getMinOrderQuantity, getProductPrice, sellsWithoutStock } from "./product-list/utils"
import catalogClasses from "./product-list/catalog.module.css"

type ProductDetailProps = {
  product: any
  /** The supplier business (logo / name for the header chip), when known. */
  supplier?: any | null
  isMultiSupplier?: boolean
}

// Spec labels: the backend sometimes fills key and keyEn with the same text.
const getSpecLabel = (key: string, keyEn: string): string => {
  const en = (keyEn || "").trim()
  if (!en || en.toLowerCase() === (key || "").trim().toLowerCase()) return key
  return `${key} (${en})`
}

function CodeChip({ label, value }: Readonly<{ label: string; value: string }>) {
  const { t } = useTranslation("order")
  return (
    <Group gap={4} wrap="nowrap">
      <Box component="span" className={classes.codeChip} translate="no">
        {label} {value}
      </Box>
      <CopyButton value={value}>
        {({ copied, copy }) => (
          <Tooltip label={copied ? t("catalog.detail.copied") : t("catalog.detail.copy")} withArrow>
            <ActionIcon variant="subtle" color="gray" aria-label={`${t("catalog.detail.copy")}: ${label}`} onClick={copy}>
              {copied ? <CheckIcon size={16} /> : <CopyIcon size={16} />}
            </ActionIcon>
          </Tooltip>
        )}
      </CopyButton>
    </Group>
  )
}

function EmptyPanel({ icon, title, hint }: Readonly<{ icon: React.ReactNode; title: string; hint: string }>) {
  return (
    <Box className={classes.emptyPanel}>
      <Box component="span" className={classes.emptyIcon}>
        {icon}
      </Box>
      <Box>
        <Text fz="var(--nw-fz-lead)" fw={650}>
          {title}
        </Text>
        <Text className={classes.meta}>{hint}</Text>
      </Box>
    </Box>
  )
}

/** Canvas OrdB_Product. */
export function ProductDetail({ product, supplier, isMultiSupplier }: ProductDetailProps) {
  const { t } = useTranslation(["order", "common"])
  const navigate = useNavigate()
  const dispatch = useDispatch()
  const { user } = useSelector((state: { auth: IAuth }) => state.auth)
  const buyerBusinessId = user?.currentBusinessId || null

  const cart = useSelector((state: { cart: ICart }) => state.cart.items)
  const cartItem = cart.find((item) => item.id === product.id && item.supplierBusinessId === product.supplierBusinessId)
  const cartQty = cartItem?.quantity || 0

  const stock = product.inventoryTotalAvialable || 0
  const minOrder = getMinOrderQuantity(product)
  const sellWithoutStock = sellsWithoutStock(product)
  const [qty, setQty] = React.useState(() => Math.max(cartQty, minOrder))

  // Breakdown at the chosen quantity, so the line total is what the cart will hold (B02).
  const [breakdown, setBreakdown] = React.useState<PriceBreakdown | undefined>(undefined)
  const [priceLoading, setPriceLoading] = React.useState(true)
  const [priceError, setPriceError] = React.useState(false)

  React.useEffect(() => {
    if (!product.supplierBusinessId || !product.id || product.isConsignment) {
      setPriceLoading(false)
      return
    }
    let cancelled = false
    setPriceLoading(true)
    const handle = setTimeout(() => {
      PricingApi.calculateBatch({
        businessId: product.supplierBusinessId,
        buyerBusinessId,
        variants: [{ variantId: product.id, quantity: qty }],
      })
        .then((res) => {
          if (cancelled) return
          const rows: PriceBreakdown[] = Array.isArray(res) ? res : (res as any)?.rows || []
          setBreakdown(rows[0])
          setPriceError(rows.length === 0)
        })
        .catch(() => {
          if (!cancelled) setPriceError(true)
        })
        .finally(() => {
          if (!cancelled) setPriceLoading(false)
        })
    }, 250)
    return () => {
      cancelled = true
      clearTimeout(handle)
    }
  }, [product.id, product.supplierBusinessId, product.isConsignment, buyerBusinessId, qty])

  const fallback = priceError ? 0 : getProductPrice(product)
  const view = describePrice(t, breakdown, breakdown && breakdown.finalPrice > 0 ? breakdown.finalPrice : fallback)
  const unitPrice = view.price

  const blockReason = priceLoading && unitPrice === 0
    ? null
    : addBlockReason(t, { price: unitPrice, stock, minOrder, sellWithoutStock }) ??
      (!sellWithoutStock && qty > stock ? t("order:catalog.reasonExceedsStock", { count: stock }) : null)

  const handleAdd = () => {
    if (blockReason || unitPrice <= 0) return
    if (cartQty > 0) {
      dispatch(updateQuantityAction({ productId: product.id, supplierBusinessId: product.supplierBusinessId, quantity: qty }))
      dispatch(updatePriceAction({ productId: product.id, supplierBusinessId: product.supplierBusinessId, price: unitPrice }))
      return
    }
    dispatch(
      addToCartAction({
        ...product,
        price: unitPrice,
        quantity: qty,
        variantId: product.id,
        variantParentId: product.id,
        supplierBusinessId: product.supplierBusinessId,
        supplierName: product.supplierName,
      }),
    )
  }

  const allImages = React.useMemo(() => {
    const images: { id: string; url: string; isMain: boolean }[] = []
    const seen = new Set<string>()
    if (product.image) {
      images.push({ id: "main", url: product.image, isMain: true })
      seen.add(product.image)
    }
    ;(Array.isArray(product.variantImages) ? product.variantImages : []).forEach((img: any) => {
      if (img.url && !seen.has(img.url)) {
        images.push({ id: img.id, url: img.url, isMain: img.isMain })
        seen.add(img.url)
      }
    })
    return images
  }, [product.image, product.variantImages])

  const name = cleanName(typeof product.nameMon === "string" ? product.nameMon : "") || "-"
  const brandName = cleanName(product.brand?.name || (typeof product.brand === "string" ? product.brand : ""))
  const categoryName = cleanName(product.category?.name || product.itemType?.name || "")
  const supplierName = supplier ? renderBusinessName(supplier, "-") : cleanName(product.supplierName) || "-"

  // ── Price source rows ──
  const selected = breakdown?.selectedPriceType
  const statusPill = (exists: boolean, used: boolean) => {
    const tone: Tone = used ? "green" : "grey"
    const label = used ? t("order:catalog.detail.statusUsed") : exists ? t("order:catalog.detail.statusUnused") : t("order:catalog.detail.statusNone")
    return <DotPill tone={tone}>{label}</DotPill>
  }
  const sourceRows = [
    {
      key: "special",
      label: t("order:catalog.detail.rowSpecial"),
      source: breakdown?.specialPrice ? breakdown.specialPrice.specialPriceName || t("order:catalog.specialPrice") : t("order:catalog.detail.noSpecial"),
      price: breakdown?.specialPrice?.calculatedPrice ?? null,
      exists: !!breakdown?.specialPrice,
      used: selected === "SPECIAL",
    },
    {
      key: "segment",
      label: t("order:catalog.detail.rowSegment"),
      source: breakdown?.segmentPrice ? breakdown.segmentPrice.segmentName || t("order:catalog.segmentPrice") : t("order:catalog.detail.noSegment"),
      price: breakdown?.segmentPrice?.calculatedPrice ?? null,
      exists: !!breakdown?.segmentPrice,
      used: selected === "SEGMENT",
    },
    {
      key: "promotion",
      label: t("order:catalog.detail.rowPromotion"),
      source: breakdown?.promotionPrice
        ? breakdown.promotionPrice.effectiveTo
          ? t("order:catalog.promoUntil", { name: breakdown.promotionPrice.promotionName, date: formatDate(breakdown.promotionPrice.effectiveTo) })
          : breakdown.promotionPrice.promotionName
        : t("order:catalog.detail.noPromotion"),
      price: breakdown?.promotionPrice?.calculatedPrice ?? null,
      exists: !!breakdown?.promotionPrice,
      used: selected === "PROMOTION",
    },
    {
      key: "base",
      label: t("order:catalog.detail.rowBase"),
      source: breakdown?.basePrice ? t("order:catalog.detail.baseSource") : t("order:catalog.detail.noBase"),
      price: breakdown?.originalBasePrice ?? null,
      exists: !!breakdown?.basePrice,
      used: selected === "BASE" && !!breakdown?.basePrice,
    },
  ]

  const promotions = [
    ...(breakdown?.promotionPrice ? [{ id: breakdown.promotionPrice.promotionId, name: breakdown.promotionPrice.promotionName, effectiveTo: breakdown.promotionPrice.effectiveTo, rule: breakdown.promotionPrice }] : []),
    ...(breakdown?.applicablePromotions || []).map((p) => ({ id: p.promotionId, name: p.promotionName, effectiveTo: p.effectiveTo, rule: p })),
  ].filter((p, i, all) => all.findIndex((x) => x.id === p.id) === i)

  const tier = breakdown?.promotionPrice?.pricingType === "TIER" ? breakdown.promotionPrice : null

  const vatTypeLabel = product.vatType ? t(`order:catalog.detail.vatTypes.${product.vatType}`, { defaultValue: "-" }) : "-"
  const taxRows: { label: string; value: React.ReactNode }[] = [
    { label: t("order:catalog.detail.vatType"), value: vatTypeLabel },
    { label: t("order:catalog.detail.vatRate"), value: product.vatPercent > 0 ? `${product.vatPercent}%` : "-" },
    // Orders compute VAT as included in the line price (B52), whatever the
    // variant's hasTax flag says; hasTax is the city tax (НХАТ), shown below.
    { label: t("order:catalog.detail.vatIncluded"), value: t("order:catalog.detail.vatIncludedValue") },
    ...(product.hasTax ? [{ label: t("order:catalog.detail.cityTax"), value: product.taxPercent > 0 ? `${product.taxPercent}%` : t("common:actions.yes") }] : []),
    { label: t("order:catalog.detail.unit"), value: product.unit || "-" },
    { label: t("order:catalog.detail.itemType"), value: cleanName(product.itemType?.name) || "-" },
    { label: t("order:catalog.detail.category"), value: cleanName(product.category?.name) || "-" },
    { label: t("order:catalog.detail.subCategory"), value: cleanName(product.subCategory?.name) || "-" },
  ]

  const inventories: any[] = Array.isArray(product.inventories) ? product.inventories : []

  const breadcrumbs = [
    { to: "/", label: t("common:breadcrumb.home") },
    { to: "/buyer-sales/new", label: t("order:catalog.title") },
    ...(categoryName ? [{ label: categoryName }] : []),
    { label: product.skuCode || name },
  ]

  return (
    <PageLayout
      title={name}
      breadcrumb={breadcrumbs}
      noFrame
      extra={[
        <Box key="supplier" component="span" className={classes.supplierChip}>
          <Avatar size={30} radius="sm" color="dark" variant="filled" src={supplier?.logo || null} alt="">
            {(supplierName.trim()[0] || "?").toUpperCase()}
          </Avatar>
          <Text span inherit truncate>
            {supplierName}
          </Text>
        </Box>,
        <Button key="back" variant="default" radius="md" h={42} leftSection={<ArrowLeftIcon size={16} />} onClick={() => navigate("/buyer-sales/new")}>
          {t("order:catalog.detail.backToCatalog")}
        </Button>,
      ]}
    >
      <Box className={classes.grid}>
        {/* ── Left ── */}
        <Box className={classes.column}>
          <Box className={classes.card}>
            <ProductGallery images={allImages} resetKey={product.id} />
          </Box>

          <Box className={classes.card}>
            <Text className={classes.cardTitle} mb="md">
              {t("order:catalog.detail.taxTitle")}
            </Text>
            <Box className={classes.kv}>
              {taxRows.map((row) => (
                <React.Fragment key={row.label}>
                  <Text className={classes.kvLabel}>{row.label}</Text>
                  <Text className={classes.kvValue}>{row.value}</Text>
                </React.Fragment>
              ))}
            </Box>
          </Box>

          <Box className={classes.cardFlush}>
            <Box className={classes.cardHead}>
              <Text className={classes.cardTitle}>{t("order:catalog.detail.stockTitle")}</Text>
            </Box>
            {inventories.length === 0 ? (
              <Text className={classes.meta} px="lg" pb="lg">
                {t("order:catalog.detail.stockEmpty")}
              </Text>
            ) : (
              <Table verticalSpacing="sm" horizontalSpacing="lg">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{t("order:catalog.detail.colWarehouse")}</Table.Th>
                    <Table.Th ta="right">{t("order:catalog.detail.colAvailable")}</Table.Th>
                    <Table.Th ta="right">{t("order:catalog.detail.colReserved")}</Table.Th>
                    <Table.Th ta="right">{t("order:catalog.detail.colTotal")}</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {inventories.map((inv, i) => (
                    <Table.Tr key={inv.id || i}>
                      <Table.Td>
                        <Text fz="var(--nw-fz-body)" fw={600}>
                          {inv.warehouseName || "-"}
                        </Text>
                        {inv.warehouseRefCode && <Text className={`${classes.meta} ${classes.mono}`}>{inv.warehouseRefCode}</Text>}
                      </Table.Td>
                      <Table.Td ta="right" className={classes.mono}>
                        {inv.availableQuantity ?? "-"}
                      </Table.Td>
                      <Table.Td ta="right" className={classes.mono}>
                        {inv.reservedQuantity ?? "-"}
                      </Table.Td>
                      <Table.Td ta="right" className={classes.mono}>
                        {inv.totalQuantity ?? "-"}
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}
            <Text className={classes.tableNote}>{t("order:catalog.detail.stockNote")}</Text>
          </Box>

          {Array.isArray(product.additionalInfo) && product.additionalInfo.length > 0 && (
            <Box className={classes.card}>
              <Text className={classes.cardTitle} mb="md">
                {t("order:catalog.detail.specs")}
              </Text>
              <Box className={classes.kv}>
                {product.additionalInfo.map((spec: { key: string; keyEn: string; value: string }, i: number) => (
                  <React.Fragment key={i}>
                    <RenderText className={classes.kvLabel} text={getSpecLabel(spec.key, spec.keyEn)} />
                    <RenderText className={classes.kvValue} text={spec.value} />
                  </React.Fragment>
                ))}
              </Box>
            </Box>
          )}
        </Box>

        {/* ── Right ── */}
        <Box className={classes.column}>
          <Box className={classes.card}>
            <Group justify="space-between" wrap="wrap" gap="sm" mb="md">
              <Text fz="var(--nw-fz-body)">
                <Text span inherit fw={650}>
                  {brandName || "-"}
                </Text>
                {categoryName ? <Text span inherit c="var(--nw-text-2)">{` · ${categoryName}`}</Text> : null}
              </Text>
              <Group gap="xs" wrap="wrap">
                <Text span className={classes.meta}>
                  {t("order:catalog.detail.code")}
                </Text>
                {product.skuCode && <CodeChip label={t("order:catalog.detail.sku")} value={product.skuCode} />}
                {product.barCode && <CodeChip label={t("order:catalog.detail.barcode")} value={product.barCode} />}
              </Group>
            </Group>

            {priceError && (
              <Box className={classes.notice} mb="sm" role="status">
                <WarningCircleIcon size={18} />
                <Text span inherit>
                  {t("order:catalog.priceFailed")}
                </Text>
              </Box>
            )}

            <Text fz="var(--nw-fz-body)" fw={600}>
              {t("order:catalog.detail.yourPrice")}
            </Text>
            <Group justify="space-between" align="flex-end" wrap="wrap" gap="sm">
              <Box>
                {priceLoading && !breakdown ? (
                  <Skeleton h={40} w={220} radius="sm" my={4} />
                ) : unitPrice > 0 ? (
                  <Group gap="xs" align="baseline" wrap="wrap">
                    <Text span className={classes.bigPrice}>
                      {currencyFormat(unitPrice)}
                    </Text>
                    {view.strike && (
                      <Text span className={classes.strike}>
                        {currencyFormat(view.strike)}
                      </Text>
                    )}
                    <Text span className={classes.meta} fw={view.sourceIsPromo ? 600 : undefined} c={view.sourceIsPromo ? "var(--nw-text)" : undefined}>
                      {view.source}
                    </Text>
                  </Group>
                ) : (
                  <>
                    <Text className={classes.bigPrice}>{priceError ? "-" : t("order:catalog.priceUnknown")}</Text>
                    <Text className={classes.meta}>
                      {priceError ? t("order:catalog.priceNotLoaded") : t("order:catalog.priceUnknownHint")}
                    </Text>
                  </>
                )}
              </Box>
              <Box ta="right">
                <Text className={classes.meta}>{product.vatPercent > 0 ? t("order:catalog.detail.vatLine", { rate: product.vatPercent }) : "-"}</Text>
                <StockLabel stock={stock} sellWithoutStock={sellWithoutStock} />
              </Box>
            </Group>

            <Text fz="var(--nw-fz-body)" fw={600} mt="lg" mb={6}>
              {t("order:catalog.qtyLabel")}
            </Text>
            <Group justify="space-between" align="flex-end" wrap="nowrap" gap="md">
              <Group gap="md" wrap="nowrap" miw={0}>
                <Box w={130} style={{ flexShrink: 0 }}>
                  <QtyStepper value={qty} onChange={setQty} min={minOrder} label={t("order:catalog.qtyLabel")} />
                </Box>
                <Box>
                  <Text className={`${classes.meta} ${classes.noWrap}`}>{t("order:catalog.detail.minOrder", { count: minOrder })}</Text>
                  {cartQty > 0 && <Text className={classes.meta}>{t("order:catalog.detail.inCartNote", { count: cartQty })}</Text>}
                </Box>
              </Group>
              <Group gap="md" wrap="nowrap" align="flex-end">
                <Box ta="right">
                  <Text className={classes.meta}>{t("order:catalog.detail.lineTotal")}</Text>
                  <Text className={classes.lineTotal}>{unitPrice > 0 ? currencyFormat(unitPrice * qty) : "-"}</Text>
                </Box>
                <ReasonTooltip reason={blockReason}>
                  <Button
                    color="brand"
                    radius="md"
                    h={42}
                    leftSection={<ShoppingCartSimpleIcon size={18} />}
                    disabled={priceLoading && unitPrice === 0}
                    onClick={handleAdd}
                  >
                    {cartQty > 0 ? t("order:catalog.detail.updateCart") : t("order:catalog.detail.addToCart")}
                  </Button>
                </ReasonTooltip>
              </Group>
            </Group>
          </Box>

          <Box className={classes.cardFlush}>
            <Box className={classes.cardHead}>
              <Text className={classes.cardTitle}>{t("order:catalog.detail.priceSourceTitle")}<HelpIcon helpKey="order.buyer-sales-product.section.price-source" variant="section" /></Text>
              <Text className={classes.meta}>{t("order:catalog.detail.priceSourceHint")}</Text>
            </Box>
            <Table verticalSpacing="sm" horizontalSpacing="lg">
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("order:catalog.detail.colPriceType")}</Table.Th>
                  <Table.Th>{t("order:catalog.detail.colSource")}</Table.Th>
                  <Table.Th ta="right">{t("order:catalog.detail.colPrice")}</Table.Th>
                  <Table.Th>{t("order:catalog.detail.colStatus")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {sourceRows.map((row) => (
                  <Table.Tr key={row.key}>
                    <Table.Td fw={650}>{row.label}</Table.Td>
                    <Table.Td>{row.source}</Table.Td>
                    <Table.Td ta="right" className={classes.mono} fw={row.used ? 700 : undefined}>
                      {row.price !== null && row.price > 0 ? currencyFormat(row.price) : "-"}
                    </Table.Td>
                    <Table.Td>{priceLoading && !breakdown ? <Skeleton h={20} w={90} /> : statusPill(row.exists, row.used)}</Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </Box>

          <Box className={classes.card}>
            <Text className={classes.cardTitle} mb="md">
              {t("order:catalog.detail.tierTitle")}
            </Text>
            {tier ? (
              <EmptyPanel
                icon={<TrendUpIcon size={20} />}
                title={tier.promotionName}
                hint={t("order:catalog.detail.tierApplied", { count: qty, price: currencyFormat(tier.calculatedPrice) })}
              />
            ) : (
              <EmptyPanel icon={<TrendUpIcon size={20} />} title={t("order:catalog.detail.tierEmpty")} hint={t("order:catalog.detail.tierEmptyHint")} />
            )}
          </Box>
        </Box>
      </Box>

      <Box className={classes.card}>
        <Text className={classes.cardTitle}>{t("order:catalog.detail.promoTitle")}</Text>
        <Text className={classes.meta} mb="md">
          {t("order:catalog.detail.promoHint")}
        </Text>
        {promotions.length === 0 ? (
          <EmptyPanel icon={<TagIcon size={20} />} title={t("order:catalog.detail.promoEmpty")} hint={t("order:catalog.detail.promoEmptyHint")} />
        ) : (
          <Box style={{ display: "flex", flexDirection: "column", gap: "var(--mantine-spacing-sm)" }}>
            {promotions.map((p) => (
              <Group key={p.id} className={classes.emptyPanel} justify="space-between" wrap="wrap">
                <Group gap="md" wrap="nowrap" miw={0}>
                  <Box component="span" className={catalogClasses.pill}>
                    {promoIcon(p.rule.pricingType)}
                    {t(`order:catalog.promoType.${p.rule.pricingType}`, { defaultValue: t("order:catalog.promoDesc.generic") })}
                  </Box>
                  <Box miw={0}>
                    <Text fz="var(--nw-fz-lead)" fw={650}>
                      {p.name || "-"}
                    </Text>
                    <Text className={classes.meta}>{describePromotion(t, p.rule)}</Text>
                  </Box>
                </Group>
                <Text className={`${classes.meta} ${classes.mono}`}>
                  {p.effectiveTo ? t("order:catalog.untilDate", { date: formatDate(p.effectiveTo) }) : "-"}
                </Text>
              </Group>
            ))}
          </Box>
        )}
      </Box>

      <Box className={classes.cardFlush}>
        <Box className={classes.cardHead}>
          <Text className={classes.cardTitle}>{t("order:catalog.detail.supplierPriceTitle")}</Text>
        </Box>
        <Table verticalSpacing="sm" horizontalSpacing="lg">
          <Table.Thead>
            <Table.Tr>
              <Table.Th>{t("order:catalog.detail.colSupplier")}</Table.Th>
              <Table.Th ta="right">{t("order:catalog.detail.colYourPrice")}</Table.Th>
              <Table.Th>{t("order:catalog.detail.colSource")}</Table.Th>
              <Table.Th>{t("order:catalog.detail.colChoice")}</Table.Th>
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            <Table.Tr>
              <Table.Td fw={650}>{supplierName}</Table.Td>
              <Table.Td ta="right" className={classes.mono}>
                {unitPrice > 0 ? currencyFormat(unitPrice) : "-"}
              </Table.Td>
              <Table.Td>{unitPrice > 0 ? view.source : "-"}</Table.Td>
              <Table.Td>
                <DotPill tone="green">{t("order:catalog.detail.current")}</DotPill>
              </Table.Td>
            </Table.Tr>
          </Table.Tbody>
        </Table>
        {isMultiSupplier && <Text className={classes.tableNote}>{t("order:catalog.detail.supplierPriceNote")}</Text>}
      </Box>
    </PageLayout>
  )
}
